import { Autocomplete, Dialog, DialogActions, DialogContent, FormControl } from "@mui/material";
import { DialogTitleWithCloseIcon } from "../../../../../common/dialog/title/DialogTitleWithCloseIcon";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { InputLabel } from "../../../../../../common/styles/muiStyled";
import { makeStyles } from '@mui/styles';
import { IPersonBadge, Nullable } from "../../../../../../common/types";
import { useCompanyBadgeActions } from "../hooks/useCompanyBadgeActions";
import { useState } from "react";
import LoadingButton from "../../../../../common/buttons/loading/LoadingButton";
import QRCode from "../../../../../search/details/download/pdf/qr/QRCode";
import { BadgeStatuses } from "../../../../../../common/enums";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onClose: () => void;
  badges: IPersonBadge[];
  badgesToPrint: IPersonBadge[];
  unpaidBadges: IPersonBadge[];
  handleOpenOverrideCameraDialog: () => void;
};

const useStyles = makeStyles({
  helperText: {
    marginTop: -23
  }
});

export const PrintDialog = ({ onClose, open, badges, badgesToPrint, unpaidBadges, handleOpenOverrideCameraDialog }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation("common");
  const { printBadges, activeBadge } = useCompanyBadgeActions();
  const [loading, setLoading] = useState(false);
  const [selectedColleague, setSelectedColleague] = useState<Nullable<IPersonBadge>>(null);

  const getOptionsForAutocomplete = () => {
    if (!badges) {
      return [];
    }
    return badges
      .filter(badge => badge.status === BadgeStatuses.ACTIVE)
      .map(badge => ({ id: badge.id, label: `${badge.contact.firstName} ${badge.contact.lastName}`, badge }));
  };

  const handleClose = () => {
    if (loading) {
      return;
    }
    onClose();
    setSelectedColleague(null);
  };

  const handleConfirmClick = async () => {
    if (unpaidBadges.length !== 0) {
      onClose();
      handleOpenOverrideCameraDialog();
    } else {
      setLoading(true);
      await printBadges(badgesToPrint);
      handleClose();
      setLoading(false);
    }
  };

  const isButtonDisabled = !selectedColleague;

  return (
    <>
      {activeBadge && <QRCode content={activeBadge.qrCodeContent} docId={"qr-code"} hidden />}
      <Dialog open={open} onClose={handleClose} maxWidth={"xs"} fullWidth>
        <DialogTitleWithCloseIcon
          title={t("companyPage.printDialog.title", { badgeCount: badgesToPrint.length })}
          handleClose={handleClose}
        />
        <DialogContent>
          <div className={classes.helperText}>{t("companyPage.printDialog.subtitle")}</div>

          <FormControl variant="standard" fullWidth sx={{ marginTop: 3 }}>
            <InputLabel>{t("companyPage.printDialog.colleagueName")}</InputLabel>
            <Autocomplete
              options={getOptionsForAutocomplete()}
              isOptionEqualToValue={(option, value) => option.id === value.badge.id}
              fullWidth
              onChange={(_: any, newValue) => {
                setSelectedColleague(newValue ? newValue.badge : null);
              }}
              renderInput={params => <TextField {...params} size={"small"} label={`${t("common.selectLabels.select")}...`}  />}
              renderOption={(props, option) => (
                <li {...props} key={option.badge.id}>
                  {option.label}
                </li>
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("common.buttonLabels.cancel")}</Button>
          <LoadingButton disabled={isButtonDisabled} loading={loading} variant="contained" onClick={handleConfirmClick}>
            {t("common.buttonLabels.confirm")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
